import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import { EditPencil } from 'iconoir-react'
import { MouseEvent, ReactElement, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { EDITABLE_CELL_HOVER } from 'app/components/table/cellInteraction'
import { notifyError } from 'app/services/notify.client'
import { Tooltip } from '/app/components/Tooltip'
import { logError } from 'lib/logging'

type Props = {
  value?: string
  defaultEmptyValue?: string
  className?: string
  handleSubmit: (value?: string) => any
  triggerEditSideEffect?: () => void
}
/**
 * InlineEditCell component allows inline editing of a cell value with a text input.
 * It provides a user interface to edit, optionally save, or cancel the changes.
 *
 * @param {string} value - The initial value of the cell.
 * @param {string} defaultEmptyValue - Value to be set if the changed value is empty.
 * @param {string} className - Additional CSS classes to apply to the text when it is not in editing mode.
 * @param {function} handleSubmit - Callback function to handle the submission of the new value. This callback should validate and save the new value.
 * @param {function} triggerEditSideEffect - Callback function to run side effect when entering to edit mode.
 * It optionally returns the updated value.
 * @returns {ReactElement} The rendered InlineEditCell component.
 */
export function InlineEditCell({
  value,
  defaultEmptyValue,
  className,
  handleSubmit,
  triggerEditSideEffect,
}: Props): ReactElement {
  const [updatedValue, setUpdatedValue] = useState<string | undefined>(value)
  const [message, setMessage] = useState<string | undefined>(undefined)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // The editor closes on blur, which fires on mousedown, so by the time the click
  // lands `isEditing` is already false and the cell would toggle straight back on.
  // A press that started while the editor was open can never open it again.
  const wasEditingOnPress = useRef<boolean>(false)
  const toggleEdit = () => {
    triggerEditSideEffect?.()
    setIsEditing((prev) => !prev)
  }
  // These cells sit inside a row whose onRowClick opens the contact drawer, so
  // every click that belongs to editing has to stop there. Without it, entering
  // edit mode or hitting save/cancel also opens the drawer over the input.
  const stopRowClick = (event: MouseEvent) => event.stopPropagation()

  const onCellMouseDown = () => {
    wasEditingOnPress.current = isEditing
  }

  const onCellClick = (event: MouseEvent) => {
    stopRowClick(event)
    const startedWhileEditing = wasEditingOnPress.current
    // Cleared here so a genuine second click still opens the editor.
    wasEditingOnPress.current = false
    if (!isEditing && !isSaving && !startedWhileEditing) toggleEdit()
  }

  const onCancelClick = () => {
    setUpdatedValue(value)
    if (inputRef.current) {
      inputRef.current.value = value ?? ``
      inputRef.current.blur()
    }
    toggleEdit()
  }

  const onSubmit = async () => {
    setIsSaving(true)
    toggleEdit()
    setUpdatedValue(updatedValue)
    try {
      const response = await handleSubmit(updatedValue!)
      if (response) setUpdatedValue(response)
    } catch (error) {
      // The editor closes before the save resolves, so a rejection would otherwise
      // disappear: nothing on screen and an unhandled rejection in the console.
      // Reopen the editor holding what the user typed so the edit is not lost and
      // they can retry. setIsEditing rather than toggleEdit, because toggleEdit
      // re-fires triggerEditSideEffect, which closes the contact drawer.
      logError(`Failed to save inline cell edit`, error)
      notifyError(`Failed to save. Your change was not applied.`, `inline-edit-save-error`)
      setIsEditing(true)
    } finally {
      // A rejected save used to leave this stuck true, which now means the cell
      // ignores every click for the rest of the session.
      setIsSaving(false)
    }
  }

  const handleKeyPress = async (event) => {
    // look for the `Enter` keyCode
    if (event.keyCode === 13 || event.which === 13) {
      await onSubmit()
    } else if (event.keyCode === 27 || event.which === 27) {
      onCancelClick()
    }
  }

  return (
    <div
      className={classNames(
        `flex h-full px-2`,
        // The editing state paints its own blue border and background, so it must
        // not also carry the interactive-surface tint.
        isEditing ? `border border-blue-600` : `cursor-pointer ${EDITABLE_CELL_HOVER}`,
      )}
      style={isEditing ? { backgroundColor: `var(--primary-alpha-8)` } : undefined}
      onMouseDown={onCellMouseDown}
      onClick={onCellClick}
    >
      <div className="relative flex w-full grow items-center justify-between">
        {isEditing ? (
          <>
            <input
              autoFocus
              ref={inputRef}
              className="grow bg-transparent"
              defaultValue={updatedValue}
              onChange={(e) => {
                if (defaultEmptyValue && e.target.value === ``) {
                  setUpdatedValue(defaultEmptyValue)
                } else setUpdatedValue(e.target.value)
              }}
              onBlur={async (e) => {
                if (e.target.value !== value) {
                  await onSubmit()
                } else onCancelClick()
              }}
              onKeyDown={handleKeyPress}
            />
            <div className="absolute right-0 flex items-center">
              <div
                className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded border bg-surface-subtle p-1.5"
                onClick={(event) => {
                  stopRowClick(event)
                  onCancelClick()
                }}
              >
                <XMarkIcon className="stroke-current text-strong" />
              </div>
              <div
                className="ml-1 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded border bg-blue-600 p-1.5"
                onClick={async (event) => {
                  stopRowClick(event)
                  await onSubmit()
                }}
              >
                <CheckIcon className="stroke-current text-on-color" />
              </div>
            </div>
          </>
        ) : (
          <>
            <Tooltip
              message={message}
              className="truncate"
              onMouseOver={(event) => {
                if (
                  event.currentTarget instanceof HTMLElement &&
                  event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                ) {
                  setMessage(value)
                } else setMessage(undefined)
              }}
            >
              <span className={twMerge(`text-ui-xs`, className)}>{updatedValue}</span>
            </Tooltip>
            <button
              disabled={isSaving}
              // Reveals on the `td`'s own `group/cell` scope; this component no
              // longer opens a local `group` that would shadow the row's.
              className="invisible flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded border bg-surface-subtle p-1.5 group-hover/cell:visible"
              // The cell already toggles on click, and this button is `invisible`,
              // so it is out of the tab order and there is no keyboard path in.
              // The handler is here only to stop the event, so the cell's own
              // handler does not toggle straight back off after the button opened it.
              onClick={!isSaving ? onCellClick : undefined}
            >
              <EditPencil className="text-strong" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
